<template>
    <BaseCard theme="cream" radius="lgr" padding="sm" class="w-full min-w-0 md:w-full max-w-[600px]" data-testid="learning-resource-widget">
        <div v-if="resource" class="flex min-w-0 flex-col gap-4">
            <span class="icon" aria-hidden="true"><i :class="contentTypeIcon(resource.content_type)"></i></span>
            <div class="flex min-w-0 flex-col gap-1">
                <h3 class="nadia-chat-bold min-w-0 text-pretty text-ink">{{ resource.title }}</h3>
                <p v-if="resource.summary" class="body-small text-ink-muted line-clamp-3">{{ resource.summary }}</p>
                <p v-if="metaParts.length" class="scaffold-details-small text-ink-muted">{{ metaParts.join(" · ") }}</p>
            </div>

            <!-- Inertia intercepts target="_blank"; a native link keeps this conversation open. -->
            <a
                v-if="resource.url"
                :href="resource.url"
                target="_blank"
                rel="noopener noreferrer"
                class="base-btn-neutral base-btn-sm self-start no-underline"
                :aria-label="t('chat.learningResource.openAriaLabel', { title: resource.title })"
                data-testid="learning-resource-link">
                {{ t("chat.learningResource.open") }}
            </a>
        </div>

        <div v-else class="flex items-center gap-3" data-testid="learning-resource-empty">
            <BaseAvatar shape="square" size="md" aria-hidden="true"><i class="bi bi-x-circle-fill"></i></BaseAvatar>
            <div class="flex min-w-0 flex-col gap-1">
                <p class="nadia-chat-bold text-ink">{{ t("chat.learningResource.emptyTitle") }}</p>
                <p class="scaffold-details-small text-ink-muted">{{ action.action_params?.message ?? t("chat.learningResource.emptyFallback") }}</p>
            </div>
        </div>
    </BaseCard>
</template>

<script setup>
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const props = defineProps({
    action: {
        type: Object,
        default: () => ({}),
    },
});

const resource = computed(() => props.action.action_params?.resource ?? null);

const metaParts = computed(() => {
    const r = resource.value;
    if (!r) {
        return [];
    }
    const parts = [];
    if (r.content_type) {
        parts.push(r.content_type.charAt(0).toUpperCase() + r.content_type.slice(1));
    }
    if (r.duration_minutes) {
        parts.push(t("chat.learningResource.duration", { minutes: r.duration_minutes }));
    }
    if (r.provider) {
        parts.push(r.provider);
    }
    return parts;
});

const contentTypeIcons = {
    article: "bi bi-file-text",
    assessment: "bi bi-ui-checks",
    book: "bi bi-book",
    course: "bi bi-mortarboard",
    event: "bi bi-calendar-event",
    podcast: "bi bi-mic",
    video: "bi bi-play-btn",
};

function contentTypeIcon(contentType) {
    return contentTypeIcons[contentType?.trim().toLowerCase() ?? ""] ?? "bi bi-collection-play";
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Neutral file tile, same as the Reflections / role-play recap cards. */
    .icon {
        @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-ink-muted;
    }
}
</style>
